import {
    Result,
    UploadFile,
    MediaFile,
    MediaFileEntity,
    MediaFileType,
} from "core/models";
import Agent from "core/services/api";
import { SelectionRequest } from "core/services/site/fragment";

type OrderFields = [string, boolean, string?, boolean?, string?, boolean?];

const listMediaFiles = async (
    accessToken: string,
    projectId: number,
    type: MediaFileType = "",
    orderFields: OrderFields = ["insertionDate", false],
    maxCount = 1000,
): Promise<Result<MediaFile[], string>> => {
    const request = {
        projectId,
        type,
        temporary: false,
        orderFields,
        maxCount,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const response = await Agent.post<MediaFileEntity[]>(
        "mediafile/list/v1",
        request,
        headers,
        "site",
    );

    if (!response.resultObject) {
        return {
            value: undefined,
            error: "unable to list mediafiles",
        };
    }

    return {
        value: response.resultObject.map((x) => new MediaFile(x)),
        error: undefined,
    };
};

const deleteMediaFile = async (
    accessToken: string,
    mediaFileID: number,
): Promise<Result<boolean, string>> => {
    const request = { id: mediaFileID };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const response = await Agent.post<string>(
        "mediafile/delete/v1",
        request,
        headers,
        "site",
    );
    if (response.messages) {
        return {
            value: undefined,
            error: "something went wrong deleting the MediaFile",
        };
    }
    return {
        value: response.resultObject !== undefined,
        error: undefined,
    };
};

const updateMediaFile = async (
    accessToken: string,
    mediaFileId: number,
    name: string,
): Promise<Result<MediaFile, string>> => {
    const request = {
        id: mediaFileId,
        name,
        UpdateFieldNames: ["name"],
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<MediaFileEntity>(
        "mediafile/update/v1",
        request,
        headers,
        "site",
    );

    if (!result.resultObject) {
        return {
            value: undefined,
            error: "unable to Edit MediaFile",
        };
    }
    return {
        value: new MediaFile(result.resultObject),
        error: undefined,
    };
};

const createMediaUploadFile = async (
    accessToken: string,
    projectID: number,
    type: MediaFileType,
    key: string,
    extension: string,
): Promise<Result<UploadFile, string>> => {
    const request = {
        projectId: projectID,
        type,
        key,
        extension,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<UploadFile>(
        "mediafile/createuploadfile/v1",
        request,
        headers,
        "site",
    );

    if (!result.resultObject) {
        return {
            value: undefined,
            error: "unable to start an upload session",
        };
    }

    return {
        value: result.resultObject,
        error: undefined,
    };
};

const createMediaFileFromUploadedFile = async (
    accessToken: string,
    projectID: number,
    channelID: number,
    type: MediaFileType,
    key: string,
    name: string,
    fileID: number,
): Promise<Result<MediaFile, string>> => {
    const request = {
        projectId: projectID,
        channelId: channelID,
        type,
        key,
        name,
        fileId: fileID,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<MediaFileEntity>(
        "mediafile/createfromfile/v1",
        request,
        headers,
        "site",
    );

    if (!result.resultObject) {
        return {
            value: undefined,
            error: undefined,
        };
    }

    return {
        value: new MediaFile(result.resultObject),
        error: undefined,
    };
};

const createMediaFile = async (
    accessToken: string,
    projectID: number,
    channelID: number,
    selection: SelectionRequest,
): Promise<Result<MediaFile, string>> => {
    const request = {
        projectId: projectID,
        channelId: channelID,
        selection,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<MediaFileEntity>(
        "mediafile/createfromselection/v1",
        request,
        headers,
        "site",
    );
    if (!result.resultObject) {
        return {
            value: undefined,
            error: "unable to create mediafile",
        };
    }
    return {
        value: new MediaFile(result.resultObject),
        error: undefined,
    };
};

const fetchMediaFile = async (
    accessToken: string,
    id: number,
): Promise<Result<MediaFile, string>> => {
    const request = {
        id,
    };
    const headers = new Headers();
    headers.append("Authorization", accessToken);
    const result = await Agent.post<MediaFileEntity>(
        "mediafile/get/v1",
        request,
        headers,
        "site",
    );
    if (!result.resultObject) {
        return {
            value: undefined,
            error: "unable to fetch mediafile",
        };
    }
    return {
        value: new MediaFile(result.resultObject),
        error: undefined,
    };
};

export {
    listMediaFiles,
    deleteMediaFile,
    createMediaFileFromUploadedFile,
    createMediaUploadFile,
    createMediaFile,
    fetchMediaFile,
    updateMediaFile,
};
